import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useJobFlowTracking } from '../../hooks/useJobFlowTracking';
import { LuTableProperties, LuFileText } from 'react-icons/lu'
import { FaChevronLeft, FaTrash } from "react-icons/fa";
import { Alert, Snackbar } from "@mui/material";
import {
    uploadCandidateResumes,
    addFromGlobalCandidates,
    uploadCsvCandidateFile,
} from "../../Redux/ActionCreators/candidateAction";
import { updateJob, uploadResumesFromUrls } from "../../Redux/ActionCreators/jobsAction";
import { CANDIDATE_SOURCE } from "../../constants/candidateSource";
import { readDirectoryFiles } from "../Candidates/AddCandidatesModal";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useSelector } from 'react-redux';

const DEFAULT_RESUME_URL = "https://gappeo.blob.core.windows.net/dev-gappeoai/20260314110652_resume_human_resources_manager.pdf";
const DEFAULT_RESUME_FILENAME = "resume_human_resources_manager.pdf";
const RESUME_SCORING_STAGE_ID = "cf1bb4ea-355b-4861-ad7b-ba52d3ce6b82";

const COUNTRY_CODES = [
    { code: "IN", dialCode: "+91",  name: "India" },
    { code: "US", dialCode: "+1",   name: "United States" },
    { code: "GB", dialCode: "+44",  name: "United Kingdom" },
    { code: "AU", dialCode: "+61",  name: "Australia" },
    { code: "CA", dialCode: "+1",   name: "Canada" },
    { code: "AE", dialCode: "+971", name: "UAE" },
    { code: "SG", dialCode: "+65",  name: "Singapore" },
    { code: "DE", dialCode: "+49",  name: "Germany" },
    { code: "FR", dialCode: "+33",  name: "France" },
    { code: "NL", dialCode: "+31",  name: "Netherlands" },
    { code: "JP", dialCode: "+81",  name: "Japan" },
    { code: "CN", dialCode: "+86",  name: "China" },
    { code: "BR", dialCode: "+55",  name: "Brazil" },
    { code: "ZA", dialCode: "+27",  name: "South Africa" },
    { code: "NG", dialCode: "+234", name: "Nigeria" },
    { code: "KE", dialCode: "+254", name: "Kenya" },
    { code: "PK", dialCode: "+92",  name: "Pakistan" },
    { code: "BD", dialCode: "+880", name: "Bangladesh" },
    { code: "MY", dialCode: "+60",  name: "Malaysia" },
    { code: "PH", dialCode: "+63",  name: "Philippines" },
    { code: "ID", dialCode: "+62",  name: "Indonesia" },
    { code: "NZ", dialCode: "+64",  name: "New Zealand" },
    { code: "SA", dialCode: "+966", name: "Saudi Arabia" },
    { code: "QA", dialCode: "+974", name: "Qatar" },
    { code: "KW", dialCode: "+965", name: "Kuwait" },
];

const MAX_CANDIDATE_ROWS = 2;
const MAX_FILES = 20;
const ALLOWED_EXTENSIONS = [".pdf", ".docx"];
const ALLOWED_MIME_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const isSuccessStatus = (status) =>
    status === "Completed" || status === "Extracted" || status === "Done" || status === "Success";

const isFailedStatus = (status) =>
    status?.toLowerCase().includes("fail") ||
    status?.toLowerCase().includes("error") ||
    status?.toLowerCase().includes("missing");

const EVENT_TYPE_LABELS = {
    'pre_screening.assigned':          'Pre Screening Assigned',
    'pre_screening.scheduled':         'Pre Screening Scheduled',
    'ai_assessments.assigned':         'AI Assessment Assigned',
    'ai_assessments.scheduled':        'AI Assessment Scheduled',
    'telephonic_screening.assigned':   'Telephonic Screening Assigned',
    'telephonic_screening.scheduled':  'Telephonic Screening Scheduled',
    'video_interview.assigned':        'Video Interview Assigned',
    'video_interview.scheduled':       'Video Interview Scheduled',
    'candidate.rejected': 'Rejected',
};

const AVATAR_COLORS = ['#4A6EE0', '#22C55E', '#F75891', '#F59E0B', '#8B5CF6', '#06B6D4'];
const getAvatarColor = (name) => {
    if (!name) return '#8A9199';
    const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};
const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
};

const filterValidFiles = (files) =>
    files.filter((file) => {
        const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
        return ALLOWED_MIME_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(ext);
    });

function FileTypeIcon({ name }) {
    const ext = name.substring(name.lastIndexOf(".")).toLowerCase();
    const isPdf = ext === ".pdf";
    const isCsv = ext === ".csv";
    const bg    = isPdf ? "#FFF0F4" : isCsv ? "#F0FFF4" : "#F0F4FF";
    const color = isPdf ? "#F75891" : isCsv ? "#16A34A" : "#4A6EE0";
    const border = isPdf ? "1px solid #F7589133" : isCsv ? "1px solid #16A34A33" : "1px solid #4A6EE033";
    return (
        <div style={{
            width: "28px", height: "28px", borderRadius: "6px",
            background: bg,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "9px", fontWeight: 700,
            color,
            border,
            flexShrink: 0,
        }}>
            {isPdf ? "PDF" : isCsv ? "CSV" : "DOC"}
        </div>
    );
}

function ProgressItem({ item }) {
    const { file_name, status, progress, ui_message } = item;
    const isSuccess = isSuccessStatus(status);
    const isFailed = isFailedStatus(status);
    const color = isSuccess ? "#22C55E" : isFailed ? "#EF4444" : "#F75891";
    const barWidth = isSuccess ? "100%" : isFailed ? "100%" : `${progress || 0}%`;

    return (
        <div style={{
            padding: "12px",
            borderRadius: "8px",
            background: "#F7F9FC",
            border: `1px solid ${isFailed ? "#FECACA" : isSuccess ? "#BBF7D0" : "#E4E9F0"}`,
            marginBottom: "8px",
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                <FileTypeIcon name={file_name || "file.pdf"} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                        margin: 0, fontSize: "13px", fontWeight: 500, color: "#2D3748",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }} title={file_name}>
                        {file_name}
                    </p>
                    <p style={{ margin: 0, fontSize: "11px", color: isFailed ? "#EF4444" : isSuccess ? "#16A34A" : "#8A9199" }}>
                        {ui_message || status}
                    </p>
                </div>
                <span style={{ fontSize: "11px", fontWeight: 600, color, flexShrink: 0 }}>
                    {isSuccess ? "Done" : isFailed ? "Failed" : `${progress || 0}%`}
                </span>
            </div>
            <div style={{ height: "4px", borderRadius: "2px", background: "#E4E9F0", overflow: "hidden" }}>
                <div style={{
                    height: "100%",
                    width: barWidth,
                    background: color,
                    transition: "width 0.4s ease",
                    borderRadius: "2px",
                }} />
            </div>
            <div style={{ marginTop: "4px", textAlign: "right" }}>
                <span style={{ fontSize: "11px", color, fontWeight: 500 }}>{status}</span>
            </div>
        </div>
    );
}

function ResumeScoreRing({ score }) {
    const r = 18;
    const circ = 2 * Math.PI * r; // ~113.1
    const pct = (score != null && score !== 0) ? Math.max(0, Math.min(100, score)) : null;
    const offset = pct != null ? circ * (1 - pct / 100) : circ;
    const strokeColor = pct == null ? '#d1d5db'
        : pct >= 75 ? '#16a34a'
        : pct >= 50 ? '#f59e0b'
        : '#dc2626';

    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px" }}>
            <div style={{ position: "relative", width: "44px", height: "44px" }}>
                <svg width="44" height="44" viewBox="0 0 44 44" style={{ transform: "rotate(-90deg)" }}>
                    <circle cx="22" cy="22" r={r} fill="none" stroke="#f3f4f6" strokeWidth="4" />
                    {pct != null && (
                        <circle cx="22" cy="22" r={r} fill="none" stroke={strokeColor} strokeWidth="4"
                            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
                    )}
                </svg>
                <div style={{
                    position: "absolute", inset: 0, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    fontSize: "10px", fontWeight: 700,
                    color: pct != null ? "#111827" : "#d1d5db",
                }}>
                    {pct != null ? `${pct}%` : "–"}
                </div>
            </div>
            <div style={{ fontSize: "9px", color: "#9ca3af", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Resume
            </div>
        </div>
    );
}

function CountryCodeSelect({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    border: "none", borderRight: "1px solid #E4E9F0",
                    background: "#F7F9FC", padding: "0 8px",
                    height: "100%", fontSize: "13px", color: "#525B66",
                    cursor: "pointer", outline: "none",
                    display: "flex", alignItems: "center", gap: "4px",
                    minWidth: "68px",
                }}
            >
                {value} <span style={{ fontSize: "9px", marginTop: "1px" }}>▾</span>
            </button>
            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0,
                    background: "#fff", border: "1px solid #E4E9F0",
                    borderRadius: "6px", zIndex: 200,
                    maxHeight: "200px", overflowY: "auto",
                    minWidth: "170px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}>
                    {COUNTRY_CODES.map((c) => (
                        <button
                            key={c.code}
                            type="button"
                            onClick={() => { onChange(c.dialCode); setOpen(false); }}
                            style={{
                                display: "block", width: "100%",
                                padding: "7px 12px", border: "none",
                                background: c.dialCode === value ? "#FFF5F9" : "transparent",
                                textAlign: "left", cursor: "pointer",
                                fontSize: "13px", color: "#2D3748",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {c.dialCode} — {c.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function Stage4InviteCandidates({ jobId, onBack, transitioning, transitionMessage, resumeUrl, resumeFilename, flowSessionId, fromJobTabs }) {
    const effectiveResumeUrl = resumeUrl || DEFAULT_RESUME_URL;
    const effectiveResumeFilename = resumeFilename || DEFAULT_RESUME_FILENAME;
    const userEmail = localStorage.getItem('email') || 'user@example.com';
    const userName = localStorage.getItem('name') || localStorage.getItem('fullName') || userEmail;

    const { trackComplete } = useJobFlowTracking({
        flowSessionId,
        stepId: 'invite_candidates',
        stepLabel: 'Invite Candidates',
        jobId,
    });
    const trackCompleteRef = useRef(trackComplete);
    useEffect(() => { trackCompleteRef.current = trackComplete; }, [trackComplete]);

    const [rows, setRows] = useState([{ email: userEmail, dialCode: '+91', number: '' }]);
    const [screeningLoading, setScreeningLoading] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const hiringStages = useSelector((state) => state.jobs.hiringStages);
    const isResumeScoringEnabled = hiringStages?.some(
        (s) => s.hiring_stage_id === RESUME_SCORING_STAGE_ID && s.is_enabled
    );

    const [screeningStarted, setScreeningStarted] = useState(false);

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [files, setFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [showCsvModal, setShowCsvModal] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [csvUploading, setCsvUploading] = useState(false);
    const [csvUploaded, setCsvUploaded] = useState(false);

    // { [resume_upload_id]: { file_name, status, progress, ui_message, candidate_name, candidate_email, candidate_phone, candidate_id } }
    const [uploadItems, setUploadItems] = useState({});
    const [removedUploadIds, setRemovedUploadIds] = useState(new Set());

    // Track IDs from the row-based default-resume upload
    const [rowUploadIds, setRowUploadIds] = useState([]);
    const [rowUploadPending, setRowUploadPending] = useState(false);

    // { [candidate_id]: { type, status, error } } — populated by global_candidate_to_job WS events
    const [addToJobStatus, setAddToJobStatus] = useState({});
    // candidate_ids submitted to addFromGlobal; cleared when all WS responses arrive
    const [pendingAddToJobIds, setPendingAddToJobIds] = useState([]);
    // { [candidate_id]: stageText } — populated by notification WS events during batch_completed wait
    const [stageAssignments, setStageAssignments] = useState({});
    const [batchCompletedPending, setBatchCompletedPending] = useState(false);
    const [continueEnabled, setContinueEnabled] = useState(false);
    const [resumeScores, setResumeScores] = useState({});

    const [snackOpen, setSnackOpen] = useState(false);
    const [snackMessage, setSnackMessage] = useState("");
    const [snackSeverity, setSnackSeverity] = useState("error");

    const fileInputRef = useRef(null);
    const addMoreInputRef = useRef(null);
    const dragCounter = useRef(0);

    const rowUploadIdsRef = useRef([]);
    const rowUploadPendingRef = useRef(false);
    const pendingAddToJobIdsRef = useRef([]);
    const jobIdRef = useRef(jobId);
    const navigateRef = useRef(navigate);
    const showSnackRef = useRef(null);
    const addFromGlobalRef = useRef(null);
    const onBackRef = useRef(onBack);

    const batchCompletedPendingRef = useRef(false);
    const jobCandidateIdToGlobalMapRef = useRef({});
    const uploadItemsRef = useRef({});
    const wsSocketRef = useRef(null);
    const wsReconnectTimerRef = useRef(null);
    const wsReconnectAttemptsRef = useRef(0);
    const wsIntentionalCloseRef = useRef(false);
    const WS_MAX_RECONNECT = 10;

    useEffect(() => {
        const handler = () => {
            wsIntentionalCloseRef.current = true;
            clearTimeout(wsReconnectTimerRef.current);
            if (wsSocketRef.current) wsSocketRef.current.close();
        };
        window.addEventListener('APP_LOGOUT', handler);
        return () => window.removeEventListener('APP_LOGOUT', handler);
    }, []);

    useEffect(() => { uploadItemsRef.current = uploadItems; }, [uploadItems]);
    useEffect(() => { rowUploadIdsRef.current = rowUploadIds; }, [rowUploadIds]);
    useEffect(() => { rowUploadPendingRef.current = rowUploadPending; }, [rowUploadPending]);
    useEffect(() => { pendingAddToJobIdsRef.current = pendingAddToJobIds; }, [pendingAddToJobIds]);
    useEffect(() => { batchCompletedPendingRef.current = batchCompletedPending; }, [batchCompletedPending]);
    useEffect(() => { jobIdRef.current = jobId; }, [jobId]);
    useEffect(() => { navigateRef.current = navigate; }, [navigate]);
    useEffect(() => { onBackRef.current = onBack; }, [onBack]);

    useEffect(() => {
        if (fromJobTabs) return;
        const updateJobStatus = async () => {
            const result = await dispatch(updateJob(jobId, { job_status_id: "14107328-840b-4ad0-904f-8a2243720f70" }));
            if (result?.__error402) {
                setSnackMessage("Active jobs limit reached.");
                setSnackSeverity("warning");
                setSnackOpen(true);
            }
        };
        updateJobStatus();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // WebSocket connection
    useEffect(() => {
        const token = localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenentId');
        const baseUrl = process.env.REACT_APP_WS_URL;

        if (!token || !tenantId || !baseUrl) return;

        wsIntentionalCloseRef.current = false;

        const connect = () => {
            if (wsIntentionalCloseRef.current) return;

            const wsUrl = `${baseUrl}?token=${encodeURIComponent(token)}`;
            const socket = new WebSocket(wsUrl);
            wsSocketRef.current = socket;

            socket.onopen = () => {
                // Stale-socket guard: ignore events from a superseded socket
                if (wsSocketRef.current !== socket) return;
                wsReconnectAttemptsRef.current = 0;
                socket.send(JSON.stringify({ action: 'join', group: `tenant-${tenantId}` }));
            };

            socket.onmessage = (event) => {
                try {
                    const parsed = JSON.parse(event.data);
                    if (parsed.event === 'resume_upload_updated' && parsed.data) {
                        window.dispatchEvent(new CustomEvent('resume_upload_updated', { detail: parsed.data }));
                    }
                    if (parsed.event === 'global_candidate_to_job') {
                        window.dispatchEvent(new CustomEvent('global_candidate_to_job', { detail: parsed }));
                    }
                    if (parsed.event === 'candidate.rejected' && parsed.data) {
                        window.dispatchEvent(new CustomEvent('candidate_rejected', { detail: parsed.data }));
                    }
                    if (parsed.type === 'notification' && parsed.data) {
                        window.dispatchEvent(new CustomEvent('ws_notification', { detail: parsed.data }));
                    }
                } catch (err) {
                    console.error('Error parsing WebSocket message:', err);
                }
            };

            socket.onclose = () => {
                // Only reconnect for the current socket — prevents React StrictMode double-run loops
                if (wsIntentionalCloseRef.current) return;
                if (wsSocketRef.current !== socket) return;
                if (wsReconnectAttemptsRef.current >= WS_MAX_RECONNECT) return;
                const delay = Math.min(30000, 1000 * Math.pow(2, wsReconnectAttemptsRef.current));
                wsReconnectAttemptsRef.current += 1;
                wsReconnectTimerRef.current = setTimeout(connect, delay);
            };

            socket.onerror = () => {
                if (wsSocketRef.current === socket) socket.close();
            };
        };

        connect();

        return () => {
            wsIntentionalCloseRef.current = true;
            clearTimeout(wsReconnectTimerRef.current);
            if (wsSocketRef.current) wsSocketRef.current.close();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // WebSocket event handler — enrich uploadItems with candidate info; detect row-upload completion
    useEffect(() => {
        const handler = (e) => {
            const d = e.detail;
            if (!d?.id) return;

            setUploadItems((prev) => {
                const existing = prev[d.id] || {};
                const next = {
                    ...prev,
                    [d.id]: {
                        file_name: d.file_name || existing.file_name,
                        status: d.status,
                        progress: d.progress,
                        ui_message: d.ui_message,
                        candidate_name: d.candidate_name || existing.candidate_name || null,
                        candidate_email: d.candidate_email || existing.candidate_email || null,
                        candidate_phone: d.candidate_phone || existing.candidate_phone || null,
                        candidate_id: d.candidate_id || existing.candidate_id || null,
                    },
                };

                // Check if the row-based upload batch has fully settled
                if (rowUploadPendingRef.current) {
                    const ids = rowUploadIdsRef.current;
                    if (ids.length > 0) {
                        const items = ids.map((id) => next[id]).filter(Boolean);
                        if (items.length === ids.length) {
                            const allSettled = items.every(
                                (item) => isSuccessStatus(item.status) || isFailedStatus(item.status)
                            );
                            if (allSettled) {
                                const candidateIds = items
                                    .filter((i) => isSuccessStatus(i.status) && i.candidate_id)
                                    .map((i) => i.candidate_id);
                                rowUploadPendingRef.current = false;
                                setTimeout(() => {
                                    setRowUploadPending(false);
                                    setRowUploadIds([]);
                                    if (candidateIds.length > 0) {
                                        addFromGlobalRef.current?.(candidateIds, CANDIDATE_SOURCE.INVITED_CANDIDATE);
                                    } else {
                                        showSnackRef.current?.("Resumes failed to process. Please try again.", "error");
                                    }
                                }, 0);
                            }
                        }
                    }
                }

                return next;
            });
        };

        window.addEventListener('resume_upload_updated', handler);
        return () => window.removeEventListener('resume_upload_updated', handler);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle per-candidate add-to-job WS status; navigate on batch_completed
    useEffect(() => {
        const handler = (e) => {
            const { type, data } = e.detail || {};

            // batch_completed → show "Added ✓", unlock nav, enable Continue button after 10s
            if (type === 'batch_completed') {
                setBatchCompletedPending(true);
                window.dispatchEvent(new CustomEvent('INVITE_SCREENING_LOCK', { detail: { active: false } }));
                setTimeout(() => {
                    setContinueEnabled(true);
                }, 5000);
                return;
            }

            // Per-candidate events require global_candidate_id
            if (!data?.global_candidate_id) return;

            const candidateId = data.global_candidate_id;
            if (data.candidate_id) {
                jobCandidateIdToGlobalMapRef.current[data.candidate_id] = candidateId;
            }
            setAddToJobStatus((prev) => ({
                ...prev,
                [candidateId]: { type, error: data.error || null },
            }));
            if (data.resume_score != null) {
                const score = data.resume_score === 'n/a' ? 0 : data.resume_score;
                setResumeScores((prev) => ({ ...prev, [candidateId]: score }));
            }
        };

        window.addEventListener('global_candidate_to_job', handler);
        return () => window.removeEventListener('global_candidate_to_job', handler);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle stage-assignment notifications
    useEffect(() => {
        const handler = (e) => {
            const data = e.detail;
            const notifCandidateId = data?.meta_data?.candidate_id;
            const candidateEmail = data?.meta_data?.data?.candidate?.email;
            const eventType = data?.event_type;
            const stageName = data?.meta_data?.data?.stage?.name;
            const stageStatus = data?.meta_data?.data?.stage?.status;

            const label = EVENT_TYPE_LABELS[eventType]
                || (stageName && stageStatus ? `${stageName} ${stageStatus}` : null)
                || (stageName ? `${stageName} Assigning` : null);
            if (!label) return;

            // Resolve the ID used in uploadItems (may differ from notification's candidate_id).
            // Match by email first; fall back to the notification's candidate_id.
            let resolvedId = notifCandidateId;
            if (candidateEmail) {
                const match = Object.values(uploadItemsRef.current).find(
                    (item) => item.candidate_email?.toLowerCase() === candidateEmail.toLowerCase() && item.candidate_id
                );
                if (match) resolvedId = match.candidate_id;
            }

            if (resolvedId) {
                setStageAssignments((prev) => ({ ...prev, [resolvedId]: label }));
            }
        };
        window.addEventListener('ws_notification', handler);
        return () => window.removeEventListener('ws_notification', handler);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle candidate.rejected WS events
    useEffect(() => {
        const handler = (e) => {
            const data = e.detail;
            if (!data?.candidate_id) return;
            const globalId = jobCandidateIdToGlobalMapRef.current[data.candidate_id];
            if (globalId) {
                setAddToJobStatus((prev) => ({
                    ...prev,
                    [globalId]: { type: 'candidate_rejected', error: null },
                }));
            }
        };
        window.addEventListener('candidate_rejected', handler);
        return () => window.removeEventListener('candidate_rejected', handler);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-enable Start Screening button when all pending candidates have settled (no batch_completed yet)
    useEffect(() => {
        if (pendingAddToJobIds.length === 0 || batchCompletedPending) return;
        const allSettled = pendingAddToJobIds.every((id) => {
            const s = addToJobStatus[id];
            return s?.type === 'candidate_completed' || s?.type === 'candidate_failed';
        });
        if (allSettled) {
            setPendingAddToJobIds([]);
            window.dispatchEvent(new CustomEvent('INVITE_SCREENING_LOCK', { detail: { active: false } }));
        }
    }, [addToJobStatus, pendingAddToJobIds, batchCompletedPending]);

    const showSnack = useCallback((message, severity = "error") => {
        setSnackMessage(message);
        setSnackSeverity(severity);
        setSnackOpen(true);
    }, []);
    showSnackRef.current = showSnack;

    const snackClosed = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackOpen(false);
    };

    // Calls /add-from-global-candidates; waits for per-candidate WS events, then navigates
    const handleAddFromGlobal = useCallback(async (candidateIds, source) => {
        setScreeningLoading(true);
        try {
            const result = await dispatch(addFromGlobalCandidates(jobIdRef.current, candidateIds, source));
            if (result?.success) {
                trackCompleteRef.current(jobIdRef.current);
                setPendingAddToJobIds(candidateIds);
                window.dispatchEvent(new CustomEvent('INVITE_SCREENING_LOCK', { detail: { active: true } }));
            } else {
                showSnackRef.current?.(result?.message || "Failed to add candidates", "error");
                window.dispatchEvent(new CustomEvent('INVITE_SCREENING_LOCK', { detail: { active: false } }));
            }
        } catch (err) {
            showSnackRef.current?.(err?.message || "Unexpected error", "error");
            window.dispatchEvent(new CustomEvent('INVITE_SCREENING_LOCK', { detail: { active: false } }));
        } finally {
            setScreeningLoading(false);
        }
    }, [dispatch]);
    addFromGlobalRef.current = handleAddFromGlobal;

    const addRow = () => setRows((prev) => [...prev, { email: '', dialCode: '+91', number: '' }]);

    const updateRow = (index, field, value) =>
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

    const removeRow = (index) => {
        if (rows.length <= 1) return;
        setRows((prev) => prev.filter((_, i) => i !== index));
    };

    // Computed
    const allRowsFilled = rows.every((r) => r.email.trim() !== '' && r.number.trim() !== '');

    const successfulCandidates = Object.entries(uploadItems).filter(
        ([id, item]) => !removedUploadIds.has(id) && item.candidate_id && isSuccessStatus(item.status)
    );

    const canStartScreening = fromJobTabs
        ? successfulCandidates.length > 0 && !rowUploadPending
        : (allRowsFilled || successfulCandidates.length > 0 || csvUploaded) && !rowUploadPending;

    const handleStartScreening = async () => {
        if (!canStartScreening || screeningLoading) return;
        setScreeningStarted(true);
        window.dispatchEvent(new CustomEvent('INVITE_SCREENING_LOCK', { detail: { active: true } }));

        // CSV-only flow: candidates already added via API, navigate directly
        if (csvUploaded && successfulCandidates.length === 0 && !allRowsFilled) {
            trackCompleteRef.current(jobId);
            navigate(`/jobs/${jobId}/candidates`);
            onBack?.();
            return;
        }

        // If modal uploads have yielded candidate profiles, add them to the job directly
        if (successfulCandidates.length > 0) {
            const candidateIds = successfulCandidates.map(([, item]) => item.candidate_id);
            await handleAddFromGlobal(candidateIds, CANDIDATE_SOURCE.RESUME_UPLOAD);
            return;
        }

        // Row-based flow: fetch the default resume, upload it, wait for WS 100%, then addFromGlobal
        setScreeningLoading(true);
        try {
            const resp = await fetch(effectiveResumeUrl);
            const blob = await resp.blob();
            const file = new File([blob], DEFAULT_RESUME_FILENAME, { type: blob.type || 'application/pdf' });

            const payload = {
                resumes: rows.map((row) => ({
                    url: effectiveResumeUrl,
                    filename: effectiveResumeFilename,
                    name: userName,
                    email: row.email.trim(),
                    phone_number: `${row.dialCode}${row.number.trim()}`,
                })),
                trigger_resume_scoring: !!isResumeScoringEnabled
            };

            const result = await dispatch(uploadResumesFromUrls(payload));
            if (result?.success) {
                const items = Array.isArray(result.data) ? result.data : [];
                const ids = items.map((i) => i.resume_upload_id).filter(Boolean);
                if (ids.length > 0) {
                    const initial = {};
                    items.forEach((item) => {
                        if (item.resume_upload_id) {
                            initial[item.resume_upload_id] = {
                                file_name: item.filename || item.file_name || DEFAULT_RESUME_FILENAME,
                                status: 'Uploading',
                                progress: 0,
                                ui_message: 'Processing resume…',
                                candidate_name: null, candidate_email: null,
                                candidate_phone: null, candidate_id: null,
                            };
                        }
                    });
                    setUploadItems((prev) => ({ ...prev, ...initial }));
                    setRowUploadIds(ids);
                    setRowUploadPending(true);
                    // button stays disabled via rowUploadPending; WS handler triggers addFromGlobal
                } else {
                    showSnack("Upload started. Processing in background…", "success");
                }
            } else {
                showSnack(result?.message || "Failed to upload resume", "error");
            }
        } catch (err) {
            showSnack(err?.message || "Failed to upload resume", "error");
        } finally {
            setScreeningLoading(false);
        }
    };

    // Modal file handling
    const addFiles = useCallback((incoming) => {
        const valid = filterValidFiles(incoming);
        setFiles((prev) => {
            const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
            const unique = valid.filter((f) => !existing.has(`${f.name}-${f.size}`));
            return [...prev, ...unique].slice(0, MAX_FILES);
        });
    }, []);

    const handleBrowse = (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length) addFiles(selected);
        e.target.value = null;
    };

    const handleAddMore = (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length) addFiles(selected);
        e.target.value = null;
    };

    const handleDragEnter = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current += 1;
        if (dragCounter.current === 1) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current === 0) setIsDragging(false);
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

    const handleDrop = async (e) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current = 0;
        setIsDragging(false);

        const items = Array.from(e.dataTransfer.items || []);
        const collected = [];

        for (const item of items) {
            if (item.kind !== "file") continue;
            const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
            if (entry) {
                if (entry.isDirectory) {
                    const nested = await readDirectoryFiles(entry);
                    collected.push(...nested);
                } else {
                    const file = item.getAsFile();
                    if (file) collected.push(file);
                }
            } else {
                const file = item.getAsFile();
                if (file) collected.push(file);
            }
        }

        addFiles(collected);
    };

    const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

    const handleUpload = async () => {
        if (!files.length) {
            showSnack("Please select at least one file.");
            return;
        }
        setUploading(true);
        try {
            const result = await dispatch(uploadCandidateResumes(files));
            if (result?.success) {
                // Pre-populate uploadItems so the right panel shows immediately
                if (Array.isArray(result.data) && result.data.length > 0) {
                    const initial = {};
                    result.data.forEach((item) => {
                        if (item.resume_upload_id) {
                            initial[item.resume_upload_id] = {
                                file_name: item.filename || item.file_name || 'Resume',
                                status: 'Uploading',
                                progress: 0,
                                ui_message: `Uploading "${item.filename || 'Resume'}"…`,
                                candidate_name: null, candidate_email: null,
                                candidate_phone: null, candidate_id: null,
                            };
                        }
                    });
                    setUploadItems((prev) => ({ ...prev, ...initial }));
                }
                setFiles([]);
                closeModal();
                showSnack("Resumes uploaded! Processing in background…", "success");
            } else {
                showSnack(result?.message || "Upload failed", "error");
            }
        } catch (err) {
            showSnack(err?.message || "Unexpected error occurred", "error");
        } finally {
            setUploading(false);
        }
    };

    const closeModal = () => {
        setShowUploadModal(false);
        setFiles([]);
        dragCounter.current = 0;
        setIsDragging(false);
    };

    const closeCsvModal = () => {
        setShowCsvModal(false);
        setCsvFile(null);
    };

    const handleCsvUpload = async () => {
        if (!csvFile || csvUploading) return;
        setCsvUploading(true);
        try {
            const result = await dispatch(uploadCsvCandidateFile(jobId, csvFile, true));
            if (result?.success) {
                setCsvUploaded(true);
                showSnack(result.message || "CSV uploaded successfully", "success");
                closeCsvModal();
            } else {
                showSnack("CSV upload failed", "error");
                console.log('else message')
            }
        } catch (err) {
            showSnack(err?.message || err?.detail || "CSV upload failed", "error");
            console.log('Catch message')
        } finally {
            setCsvUploading(false);
        }
    };

    const removeCandidate = (uploadId) => {
        setRemovedUploadIds((prev) => new Set([...prev, uploadId]));
    };

    const downloadSampleCsv = () => {
        const headers = ["name", "email", "phonenumber"];
        const csvContent = [headers].map((r) => r.join(",")).join("\n");
        const dataUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", dataUri);
        link.setAttribute("download", "sample.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const removeAllCandidates = () => {
        const toRemove = Object.entries(uploadItems)
            .filter(([id, item]) => !removedUploadIds.has(id) && isSuccessStatus(item.status))
            .map(([id]) => id);
        setRemovedUploadIds((prev) => new Set([...prev, ...toRemove]));
    };

    if (transitioning) {
        return (
            <div className="cj-stage4" style={{ pointerEvents: 'none' }}>
                <div className="cj-invite-header">
                    <p className="gradient-text-loading">{transitionMessage}</p>
                </div>
                <div className="cj-feature-card-empty-skeleton loading-gradient-border" style={{ height: '300px' }} />
            </div>
        );
    }

    const visibleItems = Object.entries(uploadItems).filter(([id]) => !removedUploadIds.has(id)).reverse();
    const visibleSuccessCount = visibleItems.filter(
        ([, item]) => isSuccessStatus(item.status) && item.candidate_name
    ).length;
    const hasSuccessToRemove = visibleItems.some(([, item]) => isSuccessStatus(item.status));
    const remainingCount = pendingAddToJobIds.filter(
        (id) => resumeScores[id] == null && addToJobStatus[id]?.type !== 'candidate_failed'
    ).length;
    const allUploadsSettled = visibleItems.length === 0 || visibleItems.every(
        ([, item]) => isSuccessStatus(item.status) || isFailedStatus(item.status)
    );

    return (
        <div className="cj-stage4" style={{padding: fromJobTabs ? '25px' : '', backgroundColor : screeningStarted && !fromJobTabs ? 'transparent' : ''}}>
            {((pendingAddToJobIds.length === 0 && !screeningStarted) ||
                (fromJobTabs && Object.values(addToJobStatus).some((s) => s?.type === 'candidate_failed'))
            ) && (
                <button className="cj-back-link" onClick={onBack}>
                    <FaChevronLeft /> {fromJobTabs ? "Back to Candidates" : "Back to AI Automation Rules"}
                </button>
            )}

            <div className="cj-invite-columns" style={{ display: "flex", gap: "32px", alignItems: "flex-start", justifyContent: (!fromJobTabs && screeningStarted) ? "center" : "flex-start" }}>
                {/* ── Left column ── */}
                {(!screeningStarted || fromJobTabs) && <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="cj-invite-header" style={{marginBottom: fromJobTabs ? '25px' : ''}}>
                        <h3 className="cj-invite-title">{fromJobTabs ? 'Add Candidates' : 'Invite Candidates'}</h3>
                        <p className="cj-invite-subtitle">
                            Upload CSV for bulk upload, docx or pdf for individual resumes
                        </p>

                        <div className="cj-upload-btns">
                            <button
                                className="cj-upload-btn"
                                onClick={() => setShowCsvModal(true)}
                                disabled={screeningLoading || rowUploadPending || pendingAddToJobIds.length > 0}
                                style={{ cursor: screeningLoading || rowUploadPending || pendingAddToJobIds.length > 0 ? "not-allowed" : "pointer" }}
                            >
                                <LuTableProperties size={18} />
                                Upload CSV
                            </button>
                            <button
                                className="cj-upload-btn"
                                onClick={() => setShowUploadModal(true)}
                                disabled={screeningLoading || rowUploadPending || pendingAddToJobIds.length > 0}
                                style={{ cursor: screeningLoading || rowUploadPending || pendingAddToJobIds.length > 0 ? "not-allowed" : "pointer" }}
                            >
                                <LuFileText size={18} />
                                Upload resume
                            </button>
                        </div>
                    </div>

                    {fromJobTabs && (
                        <div style={{ marginTop: "16px", marginBottom: fromJobTabs ? '25px' : ''}}>
                            {visibleItems.length === 0 ? (
                                <div style={{
                                    textAlign: "center", padding: "40px 24px",
                                    borderRadius: "12px", background: "#F7F9FC",
                                    border: "1px dashed #E4E9F0",
                                }}>
                                    <p style={{ margin: 0, fontSize: "14px", color: "#8A9199" }}>
                                        No candidates added yet. Use the upload buttons above to add candidates.
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    {!screeningStarted && (
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                        <p style={{ margin: 0, fontSize: "12px", fontWeight: 400, color: "#8A9199" }}>
                                            {visibleSuccessCount > 0
                                                ? `${visibleSuccessCount} Candidate${visibleSuccessCount > 1 ? 's' : ''} uploaded`
                                                : "Processing uploads…"}
                                        </p>
                                        {hasSuccessToRemove && (
                                            <button
                                                onClick={removeAllCandidates}
                                                style={{
                                                    background: "none", border: "none", cursor: "pointer",
                                                    fontSize: "14px", fontWeight: 400, color: "#8A9199", padding: 0,
                                                }}
                                            >
                                                Remove all
                                            </button>
                                        )}
                                    </div>
                                    )}
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "480px", overflowY: "auto" }}>
                                        {visibleItems.map(([id, item]) => {
                                            if (isSuccessStatus(item.status) && item.candidate_name) {
                                                const jobStatus = addToJobStatus[item.candidate_id];
                                                const isPending = pendingAddToJobIds.includes(item.candidate_id);
                                                const jobType = jobStatus?.type;
                                                const isStarted   = jobType === 'candidate_started';
                                                const isFailed    = jobType === 'candidate_failed';
                                                const isCompleted = jobType === 'candidate_completed';
                                                const isRejected  = jobType === 'candidate_rejected';
                                                const stageText   = stageAssignments[item.candidate_id];
                                                const hasStatus = stageText || isCompleted || isFailed || isRejected || batchCompletedPending;
                                                const score = resumeScores[item.candidate_id] ?? null;
                                                return (
                                                    <div key={id} style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "38px 1fr 60px auto",
                                                        alignItems: "center",
                                                        gap: "12px",
                                                        padding: "12px 16px", borderRadius: "12px", background: "#F7FAFC",
                                                        border: `1px solid ${isFailed ? '#FECACA' : isRejected ? '#FED7AA' : hasStatus ? '#BBF7D0' : '#E4E9F0'}`,
                                                    }}>
                                                        {/* Col 1: Avatar */}
                                                        <div style={{
                                                            width: "38px", height: "38px", borderRadius: "50%",
                                                            background: getAvatarColor(item.candidate_name),
                                                            color: "#fff", display: "flex", alignItems: "center",
                                                            justifyContent: "center", fontSize: "13px", fontWeight: 700,
                                                        }}>
                                                            {getInitials(item.candidate_name)}
                                                        </div>

                                                        {/* Col 2: Name + Email */}
                                                        <div style={{ minWidth: 0 }}>
                                                            <p style={{ margin: 0, fontWeight: 700, fontSize: "14px", lineHeight: '18px', color: "#313539", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {item.candidate_name}
                                                            </p>
                                                            <p style={{ margin: 0, fontSize: "12px", lineHeight: '16px', color: "#525B66", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {item.candidate_email}{item.candidate_phone ? ` · ${item.candidate_phone}` : ''}
                                                            </p>
                                                        </div>

                                                        {/* Col 3: Resume score ring */}
                                                        {screeningStarted && 
                                                            <div style={{ display: "flex", justifyContent: "center" }}>
                                                                <ResumeScoreRing score={score} />
                                                            </div>
                                                        }

                                                        {/* Col 4: Status badge */}
                                                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                            {isRejected ? (
                                                                <span style={{ fontSize: "12px", fontWeight: 600, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#FFF7ED", color: "#EA580C", border: "1px solid #FED7AA" }}>✕ Rejected</span>
                                                            ) : stageText ? (
                                                                <span style={{ fontSize: "12px", fontWeight: 600, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0" }}>✓ {stageText}</span>
                                                            ) : isCompleted ? (
                                                                <span style={{ fontSize: "12px", fontWeight: 600, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0" }}>✓ Added</span>
                                                            ) : isFailed ? (
                                                                <span style={{ fontSize: "12px", fontWeight: 600, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#FEF2F2", color: "#EF4444", border: "1px solid #FECACA" }}>× Failed</span>
                                                            ) : batchCompletedPending ? (
                                                                <span style={{ fontSize: "12px", fontWeight: 600, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0" }}>✓ Added</span>
                                                            ) : (isStarted || isPending) ? (
                                                                <span style={{ fontSize: "12px", fontWeight: 500, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#F9FAFB", color: "#6B7280", border: "1px solid #E4E9F0", display: "flex", alignItems: "center", gap: "5px" }}>
                                                                    <div className="spinner-border spinner-border-sm" role="status" style={{ color: '#F75891', width: "12px", height: "12px", borderWidth: "2px" }}><span className="visually-hidden">Loading...</span></div>
                                                                    Processing...
                                                                </span>
                                                            ) : (
                                                                <button onClick={() => removeCandidate(id)} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", flexShrink: 0 }} title="Remove">
                                                                    <img src="/images/cross-black.svg" alt="Remove" style={{ width: "16px", height: "16px" }} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return <ProgressItem key={id} item={item} />;
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {!fromJobTabs && <div className="cj-experience-card">
                        <h4 className="cj-exp-title">Experience Gappeo as a candidate</h4>
                        <p className="cj-exp-subtitle">
                            Add your mobile. We have prefilled your <br /> email id and created a sample CV.
                        </p>

                        <div className="cj-candidate-rows">
                            {rows.map((row, index) => (
                                <div key={index} className="cj-candidate-row">
                                    <input
                                        type="email"
                                        className="cj-input"
                                        value={row.email}
                                        onChange={(e) => updateRow(index, 'email', e.target.value)}
                                        placeholder="Email address"
                                    />
                                    <div className="cj-input" style={{ display: "flex", alignItems: "center", padding: 0, overflow: "visible" }}>
                                        <CountryCodeSelect
                                            value={row.dialCode}
                                            onChange={(dialCode) => updateRow(index, 'dialCode', dialCode)}
                                        />
                                        <input
                                            type="tel"
                                            value={row.number}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/[^\d\s\-()]/g, '');
                                                updateRow(index, 'number', val);
                                            }}
                                            placeholder="Mobile number"
                                            style={{
                                                border: "none", outline: "none", flex: 1,
                                                padding: "0 10px", fontSize: "13px",
                                                background: "transparent", minWidth: 0,
                                            }}
                                        />
                                    </div>
                                    <div
                                        className="cj-cv-icon"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => window.open(effectiveResumeUrl, '_blank', 'noopener,noreferrer')}
                                    >
                                        <LuFileText size={22} />
                                        <span>Your CV</span>
                                    </div>
                                    {rows.length > 1 && (
                                        <button
                                            className="cj-remove-row-btn"
                                            onClick={() => removeRow(index)}
                                            title="Remove row"
                                            style={{
                                                background: "none", border: "none", cursor: "pointer",
                                                color: "#8A9199", padding: "4px", display: "flex", alignItems: "center",
                                            }}
                                            onMouseEnter={(e) => (e.currentTarget.style.color = "#FF1C58")}
                                            onMouseLeave={(e) => (e.currentTarget.style.color = "#8A9199")}
                                        >
                                            <FaTrash size={14} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {rows.length < MAX_CANDIDATE_ROWS && (
                            <button className="cj-add-row-btn" onClick={addRow}>
                                Add a row
                            </button>
                        )}
                    </div>}
                    <div className='d-flex justify-content-center cj-mobile-sticky-bar' style={{gap:'24px'}}>
                        {batchCompletedPending ? (
                            <button
                                onClick={() => {
                                    navigateRef.current(`/jobs/${jobIdRef.current}/candidates`);
                                    onBackRef.current?.();
                                }}
                                disabled={!continueEnabled}
                                style={{
                                    padding: "10px 40px", borderRadius: "8px",
                                    background: continueEnabled ? "#F75891" : "#F7C0D6",
                                    color: "#fff", border: "none", fontWeight: 600,
                                    fontSize: "14px",
                                    cursor: continueEnabled ? "pointer" : "not-allowed",
                                    transition: "background 0.3s ease",
                                }}
                            >
                                Continue →
                            </button>
                        ) : (
                            <>
                                <button
                                    className={classNames("cj-btn-primary")}
                                    onClick={handleStartScreening}
                                    disabled={!canStartScreening || screeningLoading || rowUploadPending || pendingAddToJobIds.length > 0 || !allUploadsSettled}
                                    style={{
                                        opacity: !canStartScreening || screeningLoading || rowUploadPending || pendingAddToJobIds.length > 0 || !allUploadsSettled ? 0.5 : 1,
                                        cursor: !canStartScreening || pendingAddToJobIds.length > 0 || !allUploadsSettled ? "not-allowed" : "pointer",
                                    }}
                                >
                                    {screeningLoading || rowUploadPending || pendingAddToJobIds.length > 0 || !allUploadsSettled ? "Processing…" : "Start Screening"}{" "}
                                    <span className="cj-btn-arrow">→</span>
                                </button>

                                {/* <button
                                    className="cj-cancel-btn"
                                    onClick={onBack}
                                    disabled={screeningLoading || pendingAddToJobIds.length > 0}
                                    style={{ opacity: screeningLoading || pendingAddToJobIds.length > 0 ? 0.5 : 1 }}
                                >
                                    Cancel
                                </button> */}
                            </>
                        )}
                    </div>
                </div>}

                {!fromJobTabs && !screeningStarted && visibleItems.length > 0 && (
                    <hr className="cj-invite-divider" style={{border: 'none', background:'#525B66', height:'stretch', width:'2px', margin:'0'}} />
                )}

                {/* ── Right column — uploaded candidates ── */}
                {!fromJobTabs && (screeningStarted || visibleItems.length > 0) && (
                    <div className="cj-invite-right-col" style={screeningStarted
                        ? { minWidth: "480px", margin: "0 auto" }
                        : { flex: "0 0 480px", minWidth: 0 }
                    }>
                        {!screeningStarted && (
                            <div style={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", marginBottom: "16px",
                            }}>
                                <p style={{ margin: 0, fontSize: "12px", fontWeight: 400, color: "#8A9199" }}>
                                    {visibleSuccessCount > 0
                                        ? `${visibleSuccessCount} Candidate${visibleSuccessCount > 1 ? 's' : ''} uploaded`
                                        : "Processing uploads…"}
                                </p>
                                {hasSuccessToRemove && (
                                    <button
                                        onClick={removeAllCandidates}
                                        style={{
                                            background: "none", border: "none", cursor: "pointer",
                                            fontSize: "14px", fontWeight: 400, color: "#8A9199", padding: 0,
                                        }}
                                    >
                                        Remove all
                                    </button>
                                )}
                            </div>
                        )}

                        {screeningStarted && pendingAddToJobIds.length > 0 && (
                            <div style={{
                                background: "#fff", border: "1px solid #fce7f3", borderRadius: "12px",
                                padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px",
                            }}>
                                <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "#fce7f3", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="#e8417a" strokeWidth="1.8" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>Processing candidates</div>
                                    <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>Analysing resumes and assigning stages…</div>
                                    <div style={{ marginTop: "8px", height: "4px", background: "#f3f4f6", borderRadius: "99px", overflow: "hidden" }}>
                                        <div style={{
                                            height: "4px", background: "#e8417a", borderRadius: "99px",
                                            width: `${Math.round(((pendingAddToJobIds.length - remainingCount) / pendingAddToJobIds.length) * 100)}%`,
                                            transition: "width 0.4s ease",
                                        }} />
                                    </div>
                                </div>
                                <div style={{ textAlign: "center", flexShrink: 0 }}>
                                    <div style={{ fontSize: "22px", fontWeight: 700, color: "#e8417a", lineHeight: 1 }}>{remainingCount}</div>
                                    <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "2px" }}>remaining</div>
                                </div>
                            </div>
                        )}

                        <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "480px", overflowY: "auto" }}>
                            {visibleItems.map(([id, item]) => {
                                if (isSuccessStatus(item.status) && item.candidate_name) {
                                    const jobStatus = addToJobStatus[item.candidate_id];
                                    const isPending = pendingAddToJobIds.includes(item.candidate_id);
                                    const jobType = jobStatus?.type;
                                    const isStarted   = jobType === 'candidate_started';
                                    const isFailed    = jobType === 'candidate_failed';
                                    const isCompleted = jobType === 'candidate_completed';
                                    const isRejected  = jobType === 'candidate_rejected';
                                    const stageText   = stageAssignments[item.candidate_id];
                                    const hasStatus = stageText || isCompleted || isFailed || isRejected;
                                    const score = resumeScores[item.candidate_id] ?? null;

                                    return (
                                        <div key={id} style={{
                                            display: "grid",
                                            gridTemplateColumns: "38px 1fr 60px auto",
                                            alignItems: "center",
                                            gap: "12px",
                                            padding: "12px 16px", borderRadius: "12px", background: "#F7FAFC",
                                            border: `1px solid ${isFailed ? '#FECACA' : isRejected ? '#FED7AA' : hasStatus ? '#BBF7D0' : '#E4E9F0'}`,
                                        }}>
                                            {/* Col 1: Avatar */}
                                            <div style={{
                                                width: "38px", height: "38px", borderRadius: "50%",
                                                background: getAvatarColor(item.candidate_name),
                                                color: "#fff", display: "flex", alignItems: "center",
                                                justifyContent: "center", fontSize: "13px", fontWeight: 700,
                                            }}>
                                                {getInitials(item.candidate_name)}
                                            </div>

                                            {/* Col 2: Name + Email */}
                                            <div style={{ minWidth: 0 }}>
                                                <p style={{ margin: 0, fontWeight: 700, fontSize: "14px", lineHeight: '18px', color: "#313539", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {item.candidate_name}
                                                </p>
                                                <p style={{ margin: 0, fontSize: "12px", lineHeight: '16px', color: "#525B66", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {item.candidate_email}{item.candidate_phone ? ` · ${item.candidate_phone}` : ''}
                                                </p>
                                            </div>

                                            {/* Col 3: Resume score ring */}
                                            {screeningStarted && 
                                                <div style={{ display: "flex", justifyContent: "center" }}>
                                                    <ResumeScoreRing score={score} />
                                                </div>
                                            }

                                            {/* Col 4: Status badge */}
                                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                {isRejected ? (
                                                    <span style={{ fontSize: "12px", fontWeight: 600, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#FFF7ED", color: "#EA580C", border: "1px solid #FED7AA" }}>✕ Rejected</span>
                                                ) : stageText ? (
                                                    <span style={{ fontSize: "12px", fontWeight: 600, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0" }}>✓ {stageText}</span>
                                                ) : isCompleted ? (
                                                    <span style={{ fontSize: "12px", fontWeight: 600, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0" }}>✓ Added</span>
                                                ) : isFailed ? (
                                                    <span style={{ fontSize: "12px", fontWeight: 600, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#FEF2F2", color: "#EF4444", border: "1px solid #FECACA" }}>× Failed</span>
                                                ) : batchCompletedPending ? (
                                                    <span style={{ fontSize: "12px", fontWeight: 600, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0" }}>✓ Added</span>
                                                ) : (isStarted || isPending) ? (
                                                    <span style={{ fontSize: "12px", fontWeight: 500, flexShrink: 0, padding: "3px 10px", borderRadius: "20px", background: "#F9FAFB", color: "#6B7280", border: "1px solid #E4E9F0", display: "flex", alignItems: "center", gap: "5px" }}>
                                                        <div className="spinner-border spinner-border-sm" role="status" style={{ color: '#F75891', width: "12px", height: "12px", borderWidth: "2px" }}><span className="visually-hidden">Loading...</span></div>
                                                        Processing...
                                                    </span>
                                                ) : (
                                                    <button onClick={() => removeCandidate(id)} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", flexShrink: 0 }} title="Remove">
                                                        <img src="/images/cross-black.svg" alt="Remove" style={{ width: "16px", height: "16px" }} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }
                                return <ProgressItem key={id} item={item} />;
                            })}
                        </div>

                        {batchCompletedPending && (
                            <div style={{ textAlign: "center", marginTop: "20px" }}>
                                <button
                                    onClick={() => {
                                        navigateRef.current(`/jobs/${jobIdRef.current}/candidates`);
                                        onBackRef.current?.();
                                    }}
                                    disabled={!continueEnabled}
                                    style={{
                                        padding: "10px 40px", borderRadius: "8px",
                                        background: continueEnabled ? "#F75891" : "#F7C0D6",
                                        color: "#fff", border: "none", fontWeight: 600,
                                        fontSize: "14px",
                                        cursor: continueEnabled ? "pointer" : "not-allowed",
                                        transition: "background 0.3s ease",
                                    }}
                                >
                                    Continue →
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackOpen}
                autoHideDuration={3000}
                onClose={snackClosed}
            >
                <Alert onClose={snackClosed} severity={snackSeverity} sx={{ width: "100%" }}>
                    {snackMessage}
                </Alert>
            </Snackbar>

            {/* ── Upload CSV Modal ── */}
            {showCsvModal && (
                <div className="upload-modal">
                    <div className="modal-content" style={{ maxWidth: "520px", width: "100%" }}>
                        <div className="modal-head">
                            <div />
                            <h3>Upload CSV</h3>
                            <img
                                src="/images/close-icon.svg"
                                alt="close"
                                onClick={closeCsvModal}
                                style={{ cursor: "pointer" }}
                            />
                        </div>
                        <div style={{ padding: "20px" }}>
                            {!csvFile ? (
                                <div
                                    onClick={() => document.getElementById('csv-file-input').click()}
                                    style={{
                                        border: "2px dashed #E4E9F0", borderRadius: "12px",
                                        cursor: "pointer", minHeight: "180px",
                                        display: "flex", flexDirection: "column",
                                        alignItems: "center", justifyContent: "center",
                                        padding: "28px 24px", background: "#FAFBFC",
                                        marginBottom: "16px",
                                    }}
                                >
                                    <input
                                        id="csv-file-input"
                                        type="file"
                                        accept=".csv"
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) setCsvFile(f);
                                            e.target.value = null;
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <img src="/images/upload-icon-pink.svg" alt="" style={{ marginBottom: "8px" }} />
                                    <p style={{ margin: "4px 0" }}>
                                        <strong style={{ fontWeight: 600 }}>Click to upload</strong>{" "}or drag and drop
                                    </p>
                                    <p style={{ color: "#8A9199", fontSize: "13px", margin: "2px 0" }}>
                                        Supported: <strong style={{ color: "#525B66" }}>.csv</strong>
                                    </p>
                                </div>
                            ) : (
                                <div style={{ marginBottom: "16px" }}>
                                    <div style={{
                                        display: "flex", alignItems: "center", gap: "10px",
                                        padding: "8px 12px", borderRadius: "8px",
                                        background: "#F7F9FC", border: "1px solid #E4E9F0",
                                    }}>
                                        <FileTypeIcon name={csvFile.name} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{
                                                margin: 0, fontSize: "13px", fontWeight: 500, color: "#2D3748",
                                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                            }} title={csvFile.name}>{csvFile.name}</p>
                                            <p style={{ margin: 0, fontSize: "11px", color: "#8A9199" }}>
                                                {(csvFile.size / 1024).toFixed(1)} KB
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setCsvFile(null)}
                                            style={{ background: "none", border: "none", cursor: "pointer", color: "#8A9199", fontSize: "18px", lineHeight: 1, padding: "0 2px" }}
                                            onMouseEnter={(e) => (e.currentTarget.style.color = "#FF1C58")}
                                            onMouseLeave={(e) => (e.currentTarget.style.color = "#8A9199")}
                                            title="Remove"
                                        >×</button>
                                    </div>
                                </div>
                            )}
                            <div style={{ marginBottom: "12px", textAlign: "center" }}>
                                <p style={{ margin: 0, fontSize: "13px", color: "#8A9199" }}>
                                    <strong
                                        onClick={downloadSampleCsv}
                                        style={{ textDecoration: "underline", cursor: "pointer", color: "#F75891" }}
                                    >
                                        Click here
                                    </strong>{" "}to download the sample CSV
                                </p>
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                                <button
                                    className="upload-btn"
                                    onClick={handleCsvUpload}
                                    disabled={!csvFile || csvUploading}
                                    style={{ opacity: !csvFile || csvUploading ? 0.5 : 1 }}
                                >
                                    {csvUploading ? "Uploading…" : "Upload Candidates"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Upload Resumes Modal (single step) ── */}
            {showUploadModal && (
                <div className="upload-modal">
                    <div className="modal-content" style={{ maxWidth: "520px", width: "100%" }}>
                        <div className="modal-head">
                            <div />
                            <h3>Upload Resumes</h3>
                            <img
                                src="/images/close-icon.svg"
                                alt="close"
                                onClick={closeModal}
                                style={{ cursor: "pointer" }}
                            />
                        </div>

                        <div style={{ padding: "20px" }}>
                            {files.length === 0 ? (
                                <div
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{
                                        border: isDragging ? "2px dashed #F75891" : "2px dashed #E4E9F0",
                                        borderRadius: "12px",
                                        cursor: "pointer",
                                        minHeight: "180px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "28px 24px",
                                        transition: "border-color 0.2s, background 0.2s",
                                        background: isDragging ? "#FFF5F9" : "#FAFBFC",
                                        marginBottom: "16px",
                                    }}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf,.docx"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={handleBrowse}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <img src="/images/upload-icon-pink.svg" alt="" style={{ marginBottom: "8px" }} />
                                    <p style={{ margin: "4px 0" }}>
                                        <strong style={{ fontWeight: 600 }}>Click to upload</strong>{" "}or drag and drop
                                    </p>
                                    <p style={{ color: "#8A9199", fontSize: "13px", margin: "2px 0" }}>
                                        Supported:{" "}
                                        <strong style={{ color: "#525B66" }}>.pdf</strong>,{" "}
                                        <strong style={{ color: "#525B66" }}>.docx</strong>
                                    </p>
                                    <p style={{ color: "#B0B8C1", fontSize: "12px", margin: "2px 0" }}>
                                        Max {MAX_FILES} files
                                    </p>
                                </div>
                            ) : (
                                <div style={{ marginBottom: "16px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                                        <p style={{ margin: 0, fontWeight: 600, fontSize: "14px", color: "#525B66" }}>
                                            {files.length} file{files.length > 1 ? "s" : ""} selected
                                        </p>
                                        <span style={{ fontSize: "12px", color: "#8A9199" }}>max {MAX_FILES}</span>
                                    </div>
                                    <div style={{ maxHeight: "240px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
                                        {files.map((f, idx) => (
                                            <div key={idx} style={{
                                                display: "flex", alignItems: "center", gap: "10px",
                                                padding: "8px 12px", borderRadius: "8px",
                                                background: "#F7F9FC", border: "1px solid #E4E9F0",
                                            }}>
                                                <FileTypeIcon name={f.name} />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <p style={{
                                                        margin: 0, fontSize: "13px", fontWeight: 500, color: "#2D3748",
                                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                    }} title={f.name}>{f.name}</p>
                                                    <p style={{ margin: 0, fontSize: "11px", color: "#8A9199" }}>
                                                        {(f.size / 1024).toFixed(1)} KB
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                                                    style={{ background: "none", border: "none", cursor: "pointer", color: "#8A9199", fontSize: "18px", lineHeight: 1, padding: "0 2px" }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.color = "#FF1C58")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.color = "#8A9199")}
                                                    title="Remove"
                                                >×</button>
                                            </div>
                                        ))}
                                        {files.length < MAX_FILES && (
                                            <>
                                                <input
                                                    ref={addMoreInputRef}
                                                    type="file"
                                                    accept=".pdf,.docx"
                                                    multiple
                                                    style={{ display: "none" }}
                                                    onChange={handleAddMore}
                                                />
                                                <button
                                                    className="upload-btn"
                                                    onClick={() => addMoreInputRef.current?.click()}
                                                    style={{ border: "1px solid #111928", color: "#111928", backgroundColor: "transparent", margin: 0 }}
                                                >
                                                    + Add more files
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                                <button
                                    className="upload-btn"
                                    onClick={handleUpload}
                                    disabled={files.length === 0 || uploading}
                                    style={{ opacity: files.length === 0 || uploading ? 0.5 : 1 }}
                                >
                                    {uploading ? "Uploading…" : "Upload Candidates"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Stage4InviteCandidates
